import React, { Fragment } from 'react';
import { observer } from 'mobx-react';
import './DesignSummaryTable.scss';
import { Table } from 'reactstrap';
import { ActionsMenu } from 'cd-eft/components/Design/common/ActionsMenu';
import { mdiAlertCircleOutline } from '@mdi/js';
import Icon from '@mdi/react';
interface DataTableProps {
  headers: string[]; // A string array of the table headers
  accessVariableNameArray: string[]; // a string array of the object variable names for data => data.name is 'name', data.type.name is 'type.name'
  data: any[]; // The data object used for the table entries. for example, the pitSummaryTable
  dataModifiers?: {}; // Functions pertaining to a column that modify the data in a standard way. intention: surveyType 1->XYZ
  selectColumn: boolean; // True if there is a select column
  actionsColumn: boolean; // True if there is an action menu column
  checkboxEvent?: (event, i) => any; // The onClick callback for the select checkboxes
  actionMenuActions?: any; // The available actions/callbacks for the action menu options: vm.elementActions
}

interface ExpectedRowData {
  designElementId: number;
  designElementName: string;
  designElementTypeId: {
    id: number;
    name: string;
  };
  sessionId: string;
  surveyName: string;
}

function columnIsNarrow(headerTitle): string {
  const headerLower = (headerTitle as string).toLowerCase();
  let className = 'table-headers';

  switch (headerLower) {
    case 'select':
    case 'actions':
      className += ' narrow-td';
      break;
    default:
      break;
  }
  return className;
}

/**
 * Generates an array of table data elements representing a single object in the summary table.
 *
 * @param props The props for the whole component of type DataTableProps
 * @param rowObject The current item object represented in a row, ie pit = {name, id, type{name, id}, geometry, etc}
 * @param index the current index of the item object in the higher collection of these objects
 * @returns Table td elements for a specific object row
 */
function generateBody(props: DataTableProps, rowObject: object, index: number) {
  const arr = props.accessVariableNameArray.map((el) => {
    return el.split('.');
  });

  const body = props.headers.map((item, i) => {
    if ((item as string).toLowerCase() === 'select') {
      return (
        <td key={props.headers[i] + index} className="text-center">
          <input
            aria-labelledby={`Name${index} Type${index}`}
            id={'select' + index}
            data-testid="selectDesignElementId"
            name="select"
            // aria-label={
            //   'name: ' + item[props.accessVariableNameArray[i]] + ' , design type: ' + item[arr[i]['name']]
            // }
            checked={rowObject['checked']}
            type="checkbox"
            value={props.accessVariableNameArray[i] ? rowObject[arr[i][0]] : index}
            onChange={(event) => {
              if (props.checkboxEvent) {
                props.checkboxEvent(event, index);
              }
            }}
          />
        </td>
      );
    } else if ((item as string).toLowerCase() === 'actions') {
      return (
        <td key={props.headers[i] + index}>
          <ActionsMenu i={index} item={rowObject} elementActions={props.actionMenuActions} />
        </td>
      );
    } else {
      let value: any;
      let flagUpToDate: boolean = false;
      if (arr[i].length > 1) {
        value = rowObject;
        flagUpToDate = value.flagUpToDate
        for (let j = 0; j < arr[i].length; j++) {
          value = value[arr[i][j]];
        }
      } else {
        value = rowObject[arr[i][0]];
      }
      if (props.dataModifiers && props.dataModifiers[i]) {
        value = props.dataModifiers[i](value);
      }
      return (
        <td key={props.headers[i] + index} id={props.headers[i] + index}>
          <div className="design-type"> {value}
            {flagUpToDate && (
              <div className="flag-up-todate"><span className="warning-span"> (Invalid) </span>
                <div className="hover-text"><Icon path={mdiAlertCircleOutline} color="#ac2e36" size={1.2} />
                  <span className="tooltip-text" id="bottom">The structure is no longer valid because the base surface/survey has changed</span>
                </div></div>
            )}</div>
        </td>
      );
    }
  });

  return body;
}

const DataTableComponent = (props: DataTableProps) => {
  return (
    <Fragment>
      <Table className="set-table">
        <thead className="table-head">
          <tr>
            {props.headers.map((item, i) => {
              return (
                <th key={`${item}-${i}`} scope="col" className={columnIsNarrow(item)}>
                  {item}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {props.data
            .filter((el) => el !== undefined)
            .map((item, i) => {
              return i < props.data.length ? (
                <tr key={'key-' + i + '-' + item.designElementId}>{generateBody(props, item, i)}</tr>
              ) : null;
            })}
        </tbody>
      </Table>
    </Fragment>
  );
};

export const DesignSummaryTable = observer(DataTableComponent);
